import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Check, ShieldCheck } from "lucide-react";

import { Seo } from "@/components/Seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { SectionHeading } from "@/components/Section";
import { IMAGES, SECTORS } from "@/data/site";

/* ------------------------------------------------------------------ HERO */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative bg-navy">
      <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
        {/* Left — content */}
        <div className="container-px relative z-10 flex flex-col justify-center py-16 lg:py-24 lg:pr-16">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="overline text-brand-green"
          >
            Established 1989 · Commercial Facility Services
          </motion.span>

          <h1 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.25}>Commercial Cleaning</MaskLine>
            <MaskLine delay={0.38}>Done Exactly the Way</MaskLine>
            <MaskLine delay={0.51}>
              <span className="text-brand-green">You Want It</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-white/70"
          >
            Precision Maintenance was developed in 1989 to challenge the standard conception that all commercial
            cleaning providers were generic. As the son of immigrants, I knew that through diligence and hard work, a
            collective of like-minded individuals could ultimately raise the bar. Throughout the years, we maintained
            that commitment and challenge with our associates to provide consistent, reliable, detailed services with
            tenured crews, responsive management, and a focus on the details.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row"
          >
            <Link to="/request-a-quote" data-testid="hero-primary-cta" className="btn-primary">
              Request a Customized Cleaning Plan
              <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/25 px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-white hover:text-navy"
            >
              Talk With Our Team
            </Link>
          </motion.div>
        </div>

        {/* Right — supplied photograph */}
        <div className="relative min-h-[52vh] overflow-hidden lg:min-h-full">
          <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
            <img
              src={IMAGES.floorMachine}
              alt="Precision Maintenance associate operating a commercial floor scrubber in a retail facility"
              className="h-full w-full object-cover object-center"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/30 lg:bg-gradient-to-r lg:from-navy lg:via-navy/10 lg:to-transparent" />
        </div>
      </div>
    </section>
  );
};

/* ---------------------------------------------------- BRAND STATEMENT MARQUEE */
const BrandStatement = () => (
  <div data-testid="brand-statement" className="bg-brand-green py-4">
    <Marquee speed={45} gradient={false} autoFill>
      {SECTORS.map((s) => (
        <span key={s} className="mx-8 inline-flex items-center gap-6 font-display text-lg font-extrabold uppercase tracking-tight text-navy sm:text-xl">
          {s}
          <span className="text-navy/40">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------------------------------------------------------------- TRUST STRIP */
const TRUST = ["Since 1989", "Customized Plans", "Consistent Teams", "Management Oversight"];
const TrustStrip = () => (
  <div className="border-b border-border bg-white">
    <div className="container-px mx-auto flex max-w-[1500px] flex-wrap items-center justify-center gap-x-10 gap-y-3 py-6 lg:justify-between">
      {TRUST.map((t) => (
        <div key={t} className="flex items-center gap-3">
          <span className="h-1.5 w-1.5 flex-none bg-brand-green" />
          <span className="font-display text-sm font-semibold uppercase tracking-wide text-navy sm:text-base">{t}</span>
        </div>
      ))}
    </div>
  </div>
);

/* --------------------------------------------------- CORE PROMISE + TRUST POINTS */
const POINTS = [
  "Customized service plans and written cleaning responsibilities.",
  "Weekly customer follow-up and ongoing quality checks.",
  "Trained employees assigned to specific work sites.",
  "Management oversight and on-site evaluations.",
  "Licensed, bonded, and insured.",
  "Long-term employee and customer relationships.",
  "Responsive on-the-spot service when unexpected needs arise.",
];

const CorePromise = () => (
  <section className="container-px mx-auto max-w-[1500px] py-24">
    <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
      <div>
        <Reveal>
          <span className="overline">Our Core Promise</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display text-3xl font-bold leading-[1.05] tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Expect Us to Do Exactly What You Want
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-steel">
            Our goal is to learn how you want your facility maintained and then execute those expectations consistently.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/request-a-quote" data-testid="core-promise-cta" className="btn-dark mt-8">
            Tell Us Exactly How You Want It Cleaned <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>

      <div className="grid gap-px overflow-hidden border border-border bg-border">
        {POINTS.map((p, i) => (
          <Reveal key={i} delay={i * 0.05} className="bg-white">
            <div data-testid={`trust-point-${i}`} className="flex items-start gap-4 p-6">
              <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center bg-brand-green">
                <Check size={15} strokeWidth={3} className="text-navy" />
              </span>
              <p className="text-base leading-relaxed text-navy">{p}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ SLOGAN */
const SloganBand = () => (
  <section data-testid="slogan-band" className="relative overflow-hidden bg-navy py-20 sm:py-28 noise-overlay">
    <div className="pointer-events-none absolute -left-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-brand-green/15 blur-3xl" />
    <div className="container-px relative z-[2] mx-auto max-w-[1400px] text-center">
      <Reveal>
        <p className="font-display text-3xl font-black uppercase leading-[0.98] tracking-tight text-white [word-spacing:0.16em] sm:text-5xl lg:text-6xl">
          Relax... <span className="text-brand-green">we don't cut corners</span> — we clean 'em
        </p>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------------------------------------ SERVICE PREVIEW */
const GALLERY = [
  { src: IMAGES.officeMop, alt: "Professional crew mopping and detailing floors in a modern commercial office" },
  { src: IMAGES.striping, alt: "Applying fresh line striping to a commercial asphalt surface" },
  { src: IMAGES.painting, alt: "Facility maintenance painter pouring fresh paint during a commercial refresh" },
  { src: IMAGES.plumbing, alt: "Technician performing facility maintenance repair on chrome fixtures" },
];

const ServicePreview = () => (
  <section className="bg-cool-gray">
    <div className="container-px mx-auto max-w-[1500px] py-24">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHeading overline="Our Services" title="Detailed work across every commercial facility." />
        <Link to="/services" data-testid="services-preview-cta" className="btn-ghost w-fit">
          View Our Services <ArrowUpRight size={16} />
        </Link>
      </div>

      <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
        {GALLERY.map((g, i) => (
          <Reveal key={i} delay={(i % 4) * 0.08}>
            <SmartImage src={g.src} alt={g.alt} ratio="aspect-[4/3]" imgClassName="hover:scale-105" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------------------------------------------------- HOW WE WORK PREVIEW */
const HowWeWorkPreview = () => (
  <section className="container-px mx-auto max-w-[1500px] py-24">
    <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
      <SectionHeading overline="How We Work" title="A clear, four-step approach." />
      <Link to="/how-we-work" data-testid="how-we-work-preview-cta" className="btn-ghost w-fit">
        See How We Work <ArrowUpRight size={16} />
      </Link>
    </div>
    <div className="mt-8 grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
      {[
        { n: "01", t: "Customized Cleaning Plan" },
        { n: "02", t: "Written Schedule & Responsibilities" },
        { n: "03", t: "Consistent Site Team" },
        { n: "04", t: "Weekly Follow-Up" },
      ].map((s, i) => (
        <Reveal key={s.n} delay={i * 0.08} className="bg-white">
          <div data-testid={`process-preview-${s.n}`} className="flex h-full flex-col justify-between gap-10 p-8">
            <span className="font-display text-6xl font-black leading-none text-navy/12">{s.n}</span>
            <h3 className="font-display text-lg font-bold leading-snug tracking-tight text-navy">{s.t}</h3>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------------------------------------------------- WHY PRECISION PREVIEW */
const WhyPrecisionPreview = () => (
  <section className="bg-navy">
    <div className="container-px mx-auto grid max-w-[1500px] gap-14 py-24 lg:grid-cols-2 lg:items-center">
      <Reveal>
        <SmartImage src={IMAGES.officeMop} alt="Professional cleaning crew maintaining a modern commercial office" ratio="aspect-[4/3]" className="clip-corner" />
      </Reveal>
      <div>
        <Reveal>
          <span className="overline text-brand-green">Why Precision</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
            Built on trust, tenure and accountability.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-6 flex flex-wrap gap-3">
            {["Tenured Crews", "Responsive Management", "Focus on the Details"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider text-white/80">
                <ShieldCheck size={14} className="text-brand-green" /> {t}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/why-precision" data-testid="why-precision-preview-cta" className="btn-primary mt-8">
            Why Precision <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

/* --------------------------------------- CLIENT INTEGRITY STORY + BUYER GUIDE */
const StoryAndGuide = () => (
  <section className="container-px mx-auto max-w-[1500px] py-24">
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Client integrity story */}
      <Reveal className="group">
        <Link
          to="/client-stories"
          data-testid="client-story-card"
          className="relative flex h-full min-h-[380px] flex-col justify-end overflow-hidden bg-navy p-10"
        >
          <img
            src={IMAGES.moodMop}
            alt="Detailed after-hours floor care in a commercial facility"
            className="absolute inset-0 h-full w-full object-cover opacity-45 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" />
          <div className="relative z-10">
            <span className="overline text-brand-green">Client Stories & Proof</span>
            <h3 className="mt-4 max-w-md font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              An editorial look at integrity in the field.
            </h3>
            <span className="mt-6 inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-wider text-white">
              Read Client Stories <ArrowRight size={14} className="text-brand-green" />
            </span>
          </div>
        </Link>
      </Reveal>

      {/* Buyer guide */}
      <Reveal delay={0.1} className="group">
        <div data-testid="buyer-guide-card" className="flex h-full min-h-[380px] flex-col justify-between border border-border bg-cool-gray p-10">
          <div>
            <span className="overline">Buyer Guide</span>
            <h3 className="mt-4 max-w-md font-display text-2xl font-bold leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl">
              10 Things to Look for When Choosing a Commercial Cleaning Company
            </h3>
          </div>
          <Link to="/faq" data-testid="buyer-guide-cta" className="btn-dark mt-8 w-fit">
            Read the Buyer Guide <ArrowUpRight size={16} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------------------------------- SATISFACTION PROMISE */
const SatisfactionPromise = () => (
  <section className="bg-cool-gray">
    <div className="container-px mx-auto max-w-[1000px] py-24 text-center">
      <Reveal>
        <span className="overline">Satisfaction Promise</span>
      </Reveal>
      <Reveal delay={0.05}>
        <p className="mt-6 font-display text-2xl font-medium leading-snug tracking-tight text-navy [word-spacing:0.12em] sm:text-3xl lg:text-[2.4rem] lg:leading-[1.2]">
          Our commitment is to meet and exceed your requirements. When a problem occurs, we respond with sensitivity and
          speed and work to resolve it. Our approach is simple: treat customers and employees according to the Golden
          Rule.
        </p>
      </Reveal>
    </div>
  </section>
);

/* -------------------------------------------------------------------- FINAL CTA */
const FinalCta = () => (
  <section className="container-px mx-auto max-w-[1500px] py-20">
    <div className="relative overflow-hidden bg-navy px-8 py-16 sm:px-14 lg:px-20 lg:py-24 noise-overlay">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-green/25 blur-3xl" />
      <div className="relative z-[2] grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-end">
        <div>
          <Reveal>
            <span className="overline text-brand-green">Let's get started</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Request a Customized Cleaning Plan
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="flex flex-col gap-3 lg:items-end">
          <Link to="/request-a-quote" data-testid="final-primary-cta" className="btn-primary w-full justify-center lg:w-auto">
            Request a Customized Cleaning Plan <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/request-a-quote"
            data-testid="final-walkthrough-cta"
            className="inline-flex w-full items-center justify-center gap-2 border border-white/25 px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-white hover:text-navy lg:w-auto"
          >
            Schedule a Facility Walkthrough
          </Link>
          <Link
            to="/contact"
            data-testid="final-operations-cta"
            className="inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-wider text-white/70 underline-offset-4 transition-colors duration-200 hover:text-brand-green hover:underline lg:w-auto"
          >
            Talk With Our Operations Team
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Commercial Cleaning Done Exactly the Way You Want It"
        description="Since 1989, Precision Maintenance delivers consistent, reliable, detailed commercial cleaning with tenured crews, responsive management and a focus on the details."
        path="/"
      />
      <Hero />
      <BrandStatement />
      <TrustStrip />
      <CorePromise />
      <SloganBand />
      <ServicePreview />
      <HowWeWorkPreview />
      <WhyPrecisionPreview />
      <StoryAndGuide />
      <SatisfactionPromise />
      <FinalCta />
    </>
  );
}
